import { useEffect, useRef, useState } from 'react';
import { App, Button, Modal, Spin, Tabs, Typography, Alert } from 'antd';
import { playStoreApi, type PlayStoreIframeTokenDto } from '@/api/playStoreApi';

const { Text, Paragraph } = Typography;

interface PlayStoreIframeModalProps {
  enterpriseId: number | null;
  open: boolean;
  onClose: (registeredAny: boolean) => void;
}

/**
 * Modal con el iframe oficial de Managed Google Play.
 *
 * Soporta 4 paginas (tabs): Search apps, Private apps, Web apps, Organize.
 * Cuando el usuario selecciona una app o sube un APK privado, el iframe emite
 * un evento `onproductselect` con el packageName. Lo capturamos y llamamos al
 * backend para registrar la app en el catalogo local.
 *
 * Doc: https://developers.google.com/android/management/managed-google-play-iframe
 */
export default function PlayStoreIframeModal({
  enterpriseId,
  open,
  onClose,
}: PlayStoreIframeModalProps) {
  const { message } = App.useApp();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeApiLoadedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [tokenInfo, setTokenInfo] = useState<PlayStoreIframeTokenDto | null>(null);
  const [activeMode, setActiveMode] = useState<'PLAY_SEARCH' | 'PRIVATE_APPS' | 'WEB_APPS' | 'STORE_BUILDER'>('PLAY_SEARCH');
  const [registeredAny, setRegisteredAny] = useState(false);

  // 1. Cargar el token del backend cuando se abre el modal
  useEffect(() => {
    if (!open || !enterpriseId) return;
    setLoading(true);
    playStoreApi
      .getIframeToken(enterpriseId)
      .then((dto) => setTokenInfo(dto))
      .catch((err: { message?: string }) => {
        message.error('No se pudo obtener el token: ' + (err?.message ?? err));
        onClose(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enterpriseId]);

  // 2. Cargar gapi y montar el iframe cuando hay token + container
  useEffect(() => {
    if (!tokenInfo || !containerRef.current) return;

    let iframeInstance: { register?: (event: string, cb: (e: { packageName?: string; productId?: string }) => void, filter: unknown) => void } | null = null;

    const mountIframe = () => {
      // Limpiar contenedor antes de re-montar
      if (containerRef.current) containerRef.current.innerHTML = '';

      // Construir URL con mode=SELECT (recomendado por Google) y feature activa
      const featureParam = activeMode === 'PLAY_SEARCH' ? '' : `&feature=${activeMode}`;
      const url = `${tokenInfo.iframeUrl}?token=${encodeURIComponent(tokenInfo.token)}&mode=SELECT&showsearchbox=TRUE${featureParam}`;

      // gapi.iframes API
      const gapi = (window as unknown as { gapi?: { iframes?: { getContext: () => { openChild: (opts: unknown) => unknown }; CROSS_ORIGIN_IFRAMES_FILTER?: unknown } } }).gapi;
      if (!gapi?.iframes) {
        message.error('gapi.iframes no esta disponible. Recarga la pagina.');
        return;
      }

      iframeInstance = gapi.iframes.getContext().openChild({
        url,
        where: containerRef.current,
        attributes: {
          style: 'width:100%;height:600px;border:0;',
          scrolling: 'yes',
        },
      }) as { register?: (event: string, cb: (e: { packageName?: string; productId?: string }) => void, filter: unknown) => void };

      // Escuchar onproductselect (cuando admin aprueba/selecciona una app)
      iframeInstance.register?.(
        'onproductselect',
        async (event: { packageName?: string; productId?: string }) => {
          const pkg = event.packageName ?? event.productId;
          if (!pkg || !enterpriseId) return;
          try {
            await playStoreApi.registerApproved({
              enterpriseId,
              packageName: pkg,
              isPrivate: activeMode === 'PRIVATE_APPS' || activeMode === 'WEB_APPS',
            });
            message.success(`App ${pkg} agregada al catalogo.`);
            setRegisteredAny(true);
          } catch (err: unknown) {
            const errorObj = err as { message?: string };
            message.error('Error al registrar app: ' + (errorObj?.message ?? 'desconocido'));
          }
        },
        gapi.iframes.CROSS_ORIGIN_IFRAMES_FILTER,
      );
    };

    // Si gapi ya esta cargado (otra invocacion previa) usarlo directo
    const gapi = (window as unknown as { gapi?: { load?: (lib: string, cb: () => void) => void } }).gapi;

    if (gapi?.load) {
      gapi.load('gapi.iframes', mountIframe);
      iframeApiLoadedRef.current = true;
    } else {
      // Cargar gapi primero (script externo)
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;
      script.onload = () => {
        const loadedGapi = (window as unknown as { gapi?: { load: (lib: string, cb: () => void) => void } }).gapi;
        loadedGapi?.load('gapi.iframes', mountIframe);
        iframeApiLoadedRef.current = true;
      };
      script.onerror = () => message.error('No se pudo cargar gapi.iframes desde apis.google.com');
      document.body.appendChild(script);
    }

    return () => {
      // Limpiar al cambiar de modo / cerrar modal
      if (containerRef.current) containerRef.current.innerHTML = '';
      iframeInstance = null;
    };
  }, [tokenInfo, activeMode, enterpriseId, message]);

  return (
    <Modal
      title="Catalogo Managed Google Play"
      open={open}
      onCancel={() => onClose(registeredAny)}
      width={1000}
      destroyOnClose
      footer={[
        <Button key="close" onClick={() => onClose(registeredAny)}>
          Cerrar
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        message="Como funciona"
        description={
          <span>
            Este es el iframe oficial de Google. Al seleccionar/aprobar una app o
            subir un APK privado, automaticamente se agrega al catalogo local de la
            empresa y queda lista para asignar a perfiles.
          </span>
        }
        style={{ marginBottom: 12 }}
      />

      <Tabs
        activeKey={activeMode}
        onChange={(k) => setActiveMode(k as 'PLAY_SEARCH' | 'PRIVATE_APPS' | 'WEB_APPS' | 'STORE_BUILDER')}
        items={[
          { key: 'PLAY_SEARCH', label: 'Buscar en Play Store' },
          { key: 'PRIVATE_APPS', label: 'Subir APK privado' },
          { key: 'WEB_APPS', label: 'Apps web' },
          { key: 'STORE_BUILDER', label: 'Organizar' },
        ]}
      />

      <Spin spinning={loading}>
        <div
          ref={containerRef}
          style={{
            width: '100%',
            minHeight: 600,
            border: '1px solid #f0f0f0',
            borderRadius: 4,
            background: '#fafafa',
          }}
        >
          {!loading && !tokenInfo && (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Paragraph type="secondary">
                Esperando token...
              </Paragraph>
            </div>
          )}
        </div>
      </Spin>

      <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 12 }}>
        <Text strong>Tip:</Text> para subir un APK privado (ej: el que te da Claro), entra
        a la pestana <Text code>Subir APK privado</Text>, completa titulo y APK, y publica.
        Tarda ~10 minutos en quedar disponible. Despues volves a esta pantalla, pestana{' '}
        <Text code>Buscar en Play Store</Text>, busca por tu enterpriseId y la apruebas.
      </Paragraph>
    </Modal>
  );
}
